import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  CssBaseline,
  FormHelperText,
  Box,
} from "@mui/material";

export default function CattleEventsForm({ onNavigateToCattle, onNavigateToPregnancy }) {
  const { t } = useTranslation();
  const [cattleId, setCattleId] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [notes, setNotes] = useState("");
  const [nextActivityDate, setNextActivityDate] = useState("");
  const [completed, setCompleted] = useState(false);

  const [eventsList, setEventsList] = useState([
    { id: 1, cattleId: "COW001", eventType: "vaccination", eventDate: "2024-01-10", notes: "FMD vaccination", nextActivityDate: "2024-07-10", completed: true },
    { id: 2, cattleId: "COW002", eventType: "deworming", eventDate: "2024-02-15", notes: "Internal parasite treatment", nextActivityDate: "2024-05-15", completed: true },
    { id: 3, cattleId: "BUF001", eventType: "first_heat", eventDate: "2024-03-20", notes: "First heat observed", nextActivityDate: "", completed: false },
    { id: 4, cattleId: "COW003", eventType: "pd_test", eventDate: "2024-04-05", notes: "Pregnancy diagnosis - positive", nextActivityDate: "2024-07-05", completed: true },
    { id: 5, cattleId: "COW004", eventType: "drying", eventDate: "2024-05-12", notes: "Dry period management", nextActivityDate: "2024-08-12", completed: false },
    { id: 6, cattleId: "BUF002", eventType: "medicine_in_uterus", eventDate: "2024-06-18", notes: "Uterine infection treatment", nextActivityDate: "2024-06-25", completed: true },
    { id: 7, cattleId: "COW005", eventType: "vaccination", eventDate: "2024-07-22", notes: "Booster vaccination", nextActivityDate: "2025-01-22", completed: false },
    { id: 8, cattleId: "COW006", eventType: "other", eventDate: "2024-08-30", notes: "Hoof trimming", nextActivityDate: "2024-11-30", completed: true },
    { id: 9, cattleId: "BUF003", eventType: "deworming", eventDate: "2024-09-14", notes: "External parasite control", nextActivityDate: "2024-12-14", completed: false },
    { id: 10, cattleId: "COW007", eventType: "checkup", eventDate: "2024-10-08", notes: "General health examination", nextActivityDate: "2024-11-08", completed: true }
  ]);
  const [nextId, setNextId] = useState(11);
  const [editingId, setEditingId] = useState(null);

  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setCattleId("");
    setEventType("");
    setEventDate("");
    setNotes("");
    setNextActivityDate("");
    setCompleted(false);
    setEditingId(null);
    setErrors({});
  };

  const validate = () => {
    const temp = {};
    if (!cattleId) temp.cattleId = t("cattleEvents.cattleId") + " " + t("actions.required");
    if (!eventType) temp.eventType = t("cattleEvents.eventType") + " " + t("actions.required");
    if (!eventDate) temp.eventDate = t("cattleEvents.eventDate") + " " + t("actions.required");
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      id: editingId ?? nextId,
      cattleId,
      eventType,
      eventDate,
      notes,
      nextActivityDate,
      completed,
    };

    if (editingId) {
      setEventsList((prev) => prev.map((e) => (e.id === editingId ? payload : e)));
    } else {
      setEventsList((prev) => [...prev, payload]);
      setNextId((n) => n + 1);
    }

    resetForm();
  };

  const handleMarkComplete = (id) => {
    setEventsList((prev) =>
      prev.map((event) =>
        event.id === id ? { ...event, completed: !event.completed } : event
      )
    );
  };

  const handleEdit = (event) => {
    setEditingId(event.id);
    setCattleId(event.cattleId || "");
    setEventType(event.eventType || "");
    setEventDate(event.eventDate || "");
    setNotes(event.notes || "");
    setNextActivityDate(event.nextActivityDate || "");
    setCompleted(event.completed || false);
  };

  const handleDelete = (id) => {
    setEventsList((prev) => prev.filter((e) => e.id !== id));
    if (editingId === id) resetForm();
  };

  return (
    <>
      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card elevation={3} sx={{
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{
              fontWeight: 'bold',
              color: '#333',
              textAlign: 'center',
              mb: 3,
              fontSize: '1.6rem'
            }}>
              🐮 {editingId ? t("cattleEvents.editEvent") : t("cattleEvents.addEvent")}
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Cattle ID */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label={t("cattleEvents.cattleId")}
                    value={cattleId}
                    onChange={(e) => setCattleId(e.target.value)}
                    error={!!errors.cattleId}
                    helperText={errors.cattleId}
                    placeholder={t("cattleEvents.cattleId")}
                    sx={{ mb: 1 }}
                  />
                </Grid>

                {/* Event Type */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth error={!!errors.eventType} sx={{ mb: 1, minWidth: 250 }}>
                    <InputLabel id="event-type-label">{t("cattleEvents.eventType")}</InputLabel>
                    <Select
                      labelId="event-type-label"
                      value={eventType}
                      label={t("cattleEvents.eventType")}
                      onChange={(e) => setEventType(e.target.value)}
                    >
                      <MenuItem value="medicine_in_uterus">{t("cattleEvents.eventTypes.medicine_in_uterus")}</MenuItem>
                      <MenuItem value="deworming">{t("cattleEvents.eventTypes.deworming")}</MenuItem>
                      <MenuItem value="first_heat">{t("cattleEvents.eventTypes.first_heat")}</MenuItem>
                      <MenuItem value="pd_test">{t("cattleEvents.eventTypes.pd_test")}</MenuItem>
                      <MenuItem value="drying">{t("cattleEvents.eventTypes.drying")}</MenuItem>
                      <MenuItem value="vaccination">{t("cattleEvents.eventTypes.vaccination")}</MenuItem>
                      <MenuItem value="other">{t("cattleEvents.eventTypes.other")}</MenuItem>
                    </Select>
                    {errors.eventType && <FormHelperText>{errors.eventType}</FormHelperText>}
                  </FormControl>
                </Grid>

                {/* Completed Status */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth sx={{ mb: 1 }}>
                    <InputLabel id="completed-label">{t("cattleEvents.status")}</InputLabel>
                    <Select
                      labelId="completed-label"
                      value={completed}
                      label={t("cattleEvents.status")}
                      onChange={(e) => setCompleted(e.target.value)}
                    >
                      <MenuItem value={false}>{t("cattleEvents.pending")}</MenuItem>
                      <MenuItem value={true}>{t("cattleEvents.completed")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Event Date */}
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    label={t("cattleEvents.eventDate")}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    error={!!errors.eventDate}
                    helperText={errors.eventDate}
                    sx={{ mb: 1 }}
                  />
                </Grid>

                {/* Next Activity Date */}
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    label={t("cattleEvents.nextActivityDate")}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={nextActivityDate}
                    onChange={(e) => setNextActivityDate(e.target.value)}
                    placeholder={t("cattleEvents.nextActivityDate")}
                    sx={{ mb: 1 }}
                  />
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("cattleEvents.notes")}
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t("cattleEvents.notes")}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                {/* Buttons */}
                <Grid item xs={12}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    mt: 2
                  }}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                      sx={{
                        flex: editingId ? 1 : 'auto',
                        minHeight: 48,
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {editingId ? t("cattleEvents.updateEvent") : t("cattleEvents.saveEvent")}
                    </Button>
                    {editingId && (
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="large"
                        onClick={resetForm}
                        sx={{
                          flex: 1,
                          minHeight: 48,
                          fontSize: '1rem'
                        }}
                      >
                        {t("cattleEvents.cancelEdit")}
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* Events List */}
        {eventsList.length > 0 && (
          <Card elevation={2} sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("cattleEvents.title")}
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {eventsList.map((event, index) => (
                  <Card key={event.id} sx={{ mb: 2, p: 2, bgcolor: event.completed ? 'success.light' : 'background.paper' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="subtitle2">{t("cattleEvents.cattleId")}:</Typography>
                        <Typography variant="body1" fontWeight="medium">{event.cattleId}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="subtitle2">{t("cattleEvents.eventType")}:</Typography>
                        <Typography variant="body1">{t(`cattleEvents.eventTypes.${event.eventType}`)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="subtitle2">{t("cattleEvents.eventDate")}:</Typography>
                        <Typography variant="body1">{event.eventDate}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="subtitle2">{t("cattleEvents.nextActivityDate")}:</Typography>
                        <Typography variant="body1">{event.nextActivityDate || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="subtitle2">{t("cattleEvents.status")}:</Typography>
                        <Typography
                          variant="body1"
                          color={event.completed ? 'success.main' : 'warning.main'}
                          fontWeight="medium"
                        >
                          {event.completed ? t("cattleEvents.completed") : t("cattleEvents.pending")}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1,
                          flexWrap: 'wrap'
                        }}>
                          <Button
                            variant={event.completed ? "outlined" : "contained"}
                            color={event.completed ? "success" : "primary"}
                            size="small"
                            onClick={() => handleMarkComplete(event.id)}
                            sx={{ minWidth: 80 }}
                          >
                            {event.completed ? t("actions.undo") : t("actions.complete")}
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            onClick={() => handleEdit(event)}
                            sx={{ minWidth: 60 }}
                          >
                            {t("actions.edit")}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleDelete(event.id)}
                            sx={{ minWidth: 70 }}
                          >
                            {t("actions.delete")}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                    {event.notes && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("cattleEvents.notes")}:</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.notes}
                        </Typography>
                      </Box>
                    )}
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </>
  );
}
